import { useEffect, useRef, useState } from 'react';

import {
  transcriptorApi,
  type DocumentAnswers,
} from '@/shared/api/transcriptor';

import {
  createStoredAnswers,
  type AnswerEdit,
  type StoredAnswers,
  type StoredAnswersState,
} from '@/features/audioRecording';

// The answers a protocol document was finished with, and the clinician's
// edits of them. They are read again when `revisionKey` changes, e.g. with
// the document status, while the answers already read stay shown. Null until
// the first read finished.
export const useStoredDocumentAnswers = ({
  encounterId,
  documentId,
  revisionKey,
  onSaved,
}: {
  encounterId: string;
  documentId: string;
  revisionKey: string;
  onSaved?: () => void;
}) => {
  const [state, setState] = useState<StoredAnswersState | null>(null);
  const storedRef = useRef<StoredAnswers | null>(null);
  // The controller outlives renders; it calls the latest callback.
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  useEffect(() => {
    const stored = createStoredAnswers({
      encounterId,
      documentId,
      api: transcriptorApi,
      onChange: setState,
      onSaved: () => onSavedRef.current?.(),
    });
    storedRef.current = stored;
    setState(null);
    return () => stored.dispose();
  }, [encounterId, documentId]);

  useEffect(() => {
    void storedRef.current?.load();
  }, [encounterId, documentId, revisionKey]);

  const edit = (answerEdit: AnswerEdit) => storedRef.current?.edit(answerEdit);
  const receive = (answers: DocumentAnswers) =>
    storedRef.current?.receive(answers);

  return { state: state?.loaded ? state : null, edit, receive };
};
